import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Row, Form, Radio, Space, message, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  apiDateFormat,
  changeValueOptions,
  commaFormatter,
  getValidInstallments,
  options,
  renewFor,
} from "../../../utils";
import { PopupConfirm } from "../../../../../../atoms/Popup";
import { currencyFormat } from "../../../../../../libs/utils";
import { Loading } from "../../../modalContent";
import { InputField, InputRadio, ReactSelectField } from "../../../../../../atoms/FormElement";
import { SecurityDepositTopUp } from "../../../modalContent/security-deposit-top-up";

interface IProps {
  parentState: (obj: object) => void;
  onModalClose?: any;
  renewalFormConform?: Boolean;
}

let initialState = {
  visible: false,
  changeValue: "",
};
export interface RenewalTermFormHandle {
  submitForm: () => void;
}

interface RenewalTermFormProps {
  step: String;
  onSubmit: (body: any) => void;
}
const index = forwardRef<RenewalTermFormHandle, RenewalTermFormProps>(({ step, onSubmit }, ref) => {
  // redux states
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { bookingTerms, leaseType } = bookingDetails;
  const typeOfRenewal = useParams();
  let newRenewalType = typeOfRenewal["*"];
  let initialUpdateRentAmount = bookingTerms?.renewalAmount
    ? bookingTerms?.renewalAmount
    : bookingTerms?.annualBaseRent;
  // hooks initialization
  const navigate: any = useNavigate();
  const dispatch: any = useDispatch();
  const { leaseId = "" } = useParams();

  const [updatedContractRent, setUpdatedContractRent] = useState(0);
  const [renewForValue, setRenewFor] = useState(12);
  const [changeType, setChangeType] = useState(null);

  const form = useForm();
  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = form;

  // states
  const [{ visible, changeValue }, setState] = useState(initialState);

  const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));

  useEffect(() => {
    setValue("numberOfInstallments", {
      label: bookingTerms?.numberOfInstallments,
      value: bookingTerms?.numberOfInstallments,
    });
    updateState({ changeValue: bookingTerms?.changeValue });
    bookingTerms?.renewalAmount && updateContractRent(bookingTerms?.renewalAmount, 1);
  }, []);

  const changeValueField = getValues("changeValue");

  const handleAmount = (e: any) => {
    let updatedRent: any = "";
    const value = parseInt(e?.target?.value);
    const amountType = getValues("amountType");
    const changeValue = getValues("changeValue");
    const annualBaseRent = getValues("annualBaseRent");

    if (changeValue === "increase" && amountType === "amount") updatedRent = annualBaseRent + value;
    else if (changeValue === "decrease" && amountType === "amount") updatedRent = annualBaseRent - value;
    else if (changeValue === "increase" && amountType === "percentage")
      updatedRent = annualBaseRent + (annualBaseRent * value) / 100;
    else if (changeValue === "decrease" && amountType === "percentage")
      updatedRent = annualBaseRent - (annualBaseRent * value) / 100;

    if (!value) updatedRent = value;

    setValue("renewalAmount", updatedRent);
    updateContractRent(renewForValue);
  };

  const submitHandler = handleSubmit((data) => {
    let dataCopy = { ...data };

    dataCopy = {
      ...dataCopy,
      ...{ numberOfInstallments: dataCopy["numberOfInstallments"].value },
      ...{ numberOfRenewalTerms: dataCopy["renewFor"].value },
    };

    if (dataCopy?.renewalAmount)
      dataCopy = {
        ...dataCopy,
        ...{ renewalAmount: Math.round(dataCopy["renewalAmount"]) },
      };

    for (let key in dataCopy) {
      if (!["amountType", "changeValue", "securityDepositTopUp"].includes(key)) {
        dataCopy[key] = parseInt(dataCopy[key]);
      }
    }
    try {
      let payLoad = {
        leaseId: leaseId,
        bookingTerms: {
          ...bookingDetails.bookingTerms,
          ...dataCopy,
          billingCharges: [],
        },

        leaseTerms: {
          ...bookingDetails.leaseTerms,
          ...{
            leaseStartDate: apiDateFormat(bookingDetails?.leaseTerms?.leaseStartDate),
            leaseEndDate: apiDateFormat(bookingDetails?.leaseTerms?.leaseEndDate),
          },
        },
      };

      onSubmit(payLoad);
    } catch (error) {
      console.log(error, "Upcoming");
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  });

  // Expose submit method to parent
  useImperativeHandle(ref, () => ({
    submitForm: () => submitHandler(),
  }));
  // Watch all form values
  const watchedValues = useWatch({
    control,
  });

  useEffect(() => {}, [watchedValues]);

  const updateContractRent = (data, type = null, changeTypeParam = null) => {
    // formula Updated Annual rent / 12 * Number of months selected
    let renewalAmount = type === 1 ? data : getValues("renewalAmount");
    let annualBaseRent = type === 1 ? data : getValues("annualBaseRent");
    let updateRenewFor =
      bookingTerms?.numberOfRenewalTerms && type === 1
        ? bookingTerms?.numberOfRenewalTerms
        : data?.value
        ? data?.value
        : renewForValue;

    setChangeType(changeTypeParam);

    data?.value && setValue("numberOfInstallments", { label: "1", value: 1 });

    if (changeTypeParam === "no_change") {
      if (bookingTerms?.numberOfRenewalTerms) {
        setValue("renewFor", {
          label: `${bookingTerms?.numberOfRenewalTerms} months`,
          value: bookingTerms?.numberOfRenewalTerms,
        });
      }
    } else if (changeValueField === "no_change" && changeTypeParam) {
      updateRenewFor = bookingTerms?.numberOfRenewalTerms && type === 1 ? bookingTerms?.numberOfRenewalTerms : 12;
    }

    const getUpdatedRenewalAmount = renewalAmount ? renewalAmount : annualBaseRent;
    setRenewFor(updateRenewFor);
    let formattedRentAmount = (getUpdatedRenewalAmount / 12) * updateRenewFor;
    let updateRentAmount = formattedRentAmount > 0 ? Number(formattedRentAmount?.toFixed(2)) : 0;

    setUpdatedContractRent(updateRentAmount);
    changeTypeParam !== "no_change"
      ? setValue("contractAmount", updateRentAmount)
      : setValue(
          "contractAmount",
          bookingTerms?.renewalAmount ? bookingTerms?.renewalAmount : bookingTerms?.annualBaseRent
        );
  };
  //  set initial value of renewal terms For
  const setRenewForinitialTerms = () => {
    if (bookingTerms?.numberOfRenewalTerms) {
      return {
        label: `${bookingTerms?.numberOfRenewalTerms} months`,
        value: bookingTerms?.numberOfRenewalTerms,
      };
    } else {
      return renewFor[renewFor?.length - 1];
    }
  };

  const setHelpingTextForApproveRent = (type) => {
    let diff = null;
    let updateAnnaulRent = watchedValues?.renewalAmount ? watchedValues?.renewalAmount : initialUpdateRentAmount;
    if (type === 1) {
      return bookingDetails?.approvedAnnualRent
        ? `Approved Annual Rent ${currencyFormat(bookingDetails?.approvedAnnualRent)}`
        : `Approved annual rent not available`;
    } else {
      if (bookingDetails?.approvedAnnualRent && bookingDetails?.approvedAnnualRent < updateAnnaulRent) {
        diff = updateAnnaulRent - bookingDetails?.approvedAnnualRent;
        return `${currencyFormat(diff)} above approved rent`;
      } else if (bookingDetails?.approvedAnnualRent && bookingDetails?.approvedAnnualRent > updateAnnaulRent) {
        diff = updateAnnaulRent - bookingDetails?.approvedAnnualRent;
        return ` ${currencyFormat(diff)} below approved rent`;
      }
    }
  };

  return (
    <FormProvider {...form}>
      <Form
        layout="vertical"
        name="bookingform"
        scrollToFirstError
        className="bf-main"
        onFinish={handleSubmit(onSubmit)}
      >
        <Loading visible={visible} />

        <Row gutter={[16, 16]}>
          {/* <Col span={24}> */}
          <Col span={8}>
            <InputField
              disabled={true}
              control={control}
              fieldname="renewalTerm"
              label="Renewal Term"
              initValue={"12 months"}
            />
          </Col>
          <Col span={8}>
            <ReactSelectField
              control={control}
              label="Renew For"
              fieldname="renewFor"
              selectOption={renewFor}
              initValue={setRenewForinitialTerms()}
              onChange={(option) => updateContractRent(option)}
              iProps={{
                placeholder: "Select one",
                isDisabled: bookingDetails?.isAgp?.toLowerCase() === "yes" ? true : false,
              }}
            />
          </Col>
          <Col span={8}>
            <InputField
              disabled={true}
              control={control}
              fieldname="annualBaseRent"
              label="Current Annual Rent"
              initValue={bookingTerms?.annualBaseRent}
              helpingText={setHelpingTextForApproveRent(1)}
              iProps={{
                formatter: (value: string): any => commaFormatter(value),
              }}
            />
          </Col>
          <Col span={8}>
            <ReactSelectField
              control={control}
              isRequired={true}
              label="No. of Instalments"
              fieldname="numberOfInstallments"
              selectOption={getValidInstallments(renewForValue ? renewForValue : 12)}
              iProps={{
                placeholder: "Select one",
                isDisabled: bookingDetails?.isAgp?.toLowerCase() === "yes" ? true : false,
              }}
              validate={errors.numberOfInstallments && "error"}
              rules={{ required: "Please select no. of installments" }}
              validMessage={errors.numberOfInstallments && errors.numberOfInstallments.message}
            />
          </Col>
          <Col span={8}>
            <InputRadio
              disabled={bookingDetails?.isAgp?.toLowerCase() === "yes"}
              isRequired={true}
              control={control}
              fieldname="changeValue"
              initValue={newRenewalType && bookingDetails?.leaseType !== "extension" ? "" : bookingTerms?.changeValue}
              label="Increase or decrease your rent?"
              validate={errors.changeValue && "error"}
              validMessage={errors.changeValue && errors.changeValue.message}
              rules={{ required: "This field is required" }}
              onChange={(e: any) => {
                updateState({ changeValue: e.target.value });
                setValue("amountChange", "");
                if (e.target.value) {
                  if (bookingTerms?.renewalAmount && !bookingTerms?.contractRent) {
                    setValue("renewalAmount", bookingTerms?.renewalAmount);
                    setValue("contractAmount", bookingTerms?.renewalAmount);
                    updateContractRent(bookingTerms?.renewalAmount, 1, e.target.value);
                  } else if (bookingTerms?.renewalAmount && bookingTerms?.contractRent) {
                    setValue("contractAmount", bookingTerms?.contractRent);
                  } else {
                    setValue("renewalAmount", bookingTerms?.annualBaseRent);
                    setValue("contractAmount", bookingTerms?.annualBaseRent);
                    updateContractRent(bookingTerms?.annualBaseRent, 1, e.target.value);
                  }
                }
              }}
              options={
                <Row gutter={[16, 16]}>
                  {changeValueOptions.map((option, i) => (
                    <Col span={8} key={`${option.value}-${i}`}>
                      <Radio value={option.value}>{option.label}</Radio>
                    </Col>
                  ))}
                </Row>
              }
            />
          </Col>

          {changeValue && changeValue !== "no_change" && (
            <>
              <Col span={8}>
                <InputRadio
                  disabled={bookingDetails?.isAgp?.toLowerCase() === "yes"}
                  isRequired={true}
                  control={control}
                  fieldname="amountType"
                  label="Type of Adjustment"
                  initValue={
                    newRenewalType && bookingDetails?.leaseType !== "extension" ? "" : bookingTerms?.amountType
                  }
                  validate={errors.amountType && "error"}
                  validMessage={errors.amountType && errors.amountType.message}
                  rules={{
                    required: "This field is required",
                  }}
                  onChange={() => {
                    setValue("amountChange", "");
                    setValue("renewalAmount", "");
                  }}
                  options={
                    <Row gutter={[16, 16]}>
                      {options.map((option, i) => (
                        <Col span={8} key={`${option.value}-${i}`}>
                          <Radio value={option.value}>{option.label}</Radio>
                        </Col>
                      ))}
                    </Row>
                  }
                />
              </Col>
              <Col span={8}>
                <InputField
                  disabled={bookingDetails?.isAgp?.toLowerCase() === "yes"}
                  isRequired={true}
                  control={control}
                  onChange={(e) => handleAmount(e)}
                  fieldname="amountChange"
                  label="Amount of Adjustment"
                  initValue={
                    newRenewalType && bookingDetails?.leaseType !== "extension" ? "" : bookingTerms?.amountChange
                  }
                  validate={errors.amountChange && "error"}
                  // disabled={getValues("amountType") ? false : true}
                  validMessage={errors.amountChange && errors.message}
                  rules={{
                    required: "This field is required",
                  }}
                />
              </Col>
            </>
          )}

          {changeValue && (
            <>
              <Col span={8}>
                <InputField
                  disabled={true}
                  isRequired={true}
                  control={control}
                  fieldname="renewalAmount"
                  initValue={
                    newRenewalType && bookingDetails?.leaseType !== "extension" ? "" : bookingTerms?.renewalAmount
                  }
                  validate={errors.renewalAmount && "error"}
                  validMessage={errors.renewalAmount && errors.message}
                  label={changeValue === "no_change" ? "Annual Rent Amount" : "Updated Annual Rent"}
                  rules={{
                    required: "This field is required",
                  }}
                  iProps={{
                    formatter: (value: string): any => commaFormatter(value),
                  }}
                  helpingText={setHelpingTextForApproveRent(2)}
                />
              </Col>

              <Col span={8}>
                <InputField
                  disabled={true}
                  isRequired={true}
                  control={control}
                  setValue={setValue}
                  fieldname="contractAmount"
                  initValue={
                    updatedContractRent > 0
                      ? Number(updatedContractRent)?.toFixed(2)
                      : Number(bookingTerms?.renewalAmount)?.toFixed(2)
                  }
                  validate={errors.contractAmount && "error"}
                  validMessage={errors.contractAmount && errors.message}
                  label={"Updated Contract Rent"}
                  rules={{
                    required: "This field is required",
                  }}
                  iProps={{
                    formatter: (value: string): any => commaFormatter(value),
                  }}
                />
              </Col>
            </>
          )}

          {/* <Row> */}
          {/* <Col span={24}>
              <SecurityDepositTopUp />
            </Col> */}
          {/* </Row> */}
          {/* </Col> */}
        </Row>
      </Form>
    </FormProvider>
  );
});

export default index;
